import React, { useRef, useState } from "react";

import dayjs from "dayjs";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { formatDate, parseDate, dateDiff, maskDate } from "../../../helpers/dates";

import calendarIcon from "../../../assets/images/icons/calendar.svg";
import { logError } from "../../../helpers/logging";
import { DISPLAY_DATE_FORMAT } from "../../../helpers/constants";
import { checkValidDate } from "../../../helpers/validations";
import {
  AliasDatePickerHolder,
  DateInput,
  DateInputFieldLabel,
  DatePickerIcon,
  DatePickerIconHolder,
  DayPickerBackground,
  DayPickerWrapper,
  InputErrorBox,
} from "./styles";

// react-day-picker's month math runs date-fns over `selected`/`defaultMonth`;
// an Invalid Date (getTime() === NaN) drives it onto NaN-time arithmetic, a
// suspected contributor to the WebKit render-process crash. Never pass one.
const isValidDate = (date?: Date): date is Date => !!date && !Number.isNaN(date.getTime());

interface DatePickerProps {
  handleIncorrectDateChange: () => void;
  placeholder?: string;
  onChange?: (date: string) => void;
  toYear?: number;
  fromYear?: number;
  displayMonth?: Date;
  serverValidation?: string[];
  disabledDays?: { disabledBeforeDate: Date; disabledAfterDate: Date };
  onEditDate?: () => void;
  labelTop?: string;
  className?: string;
  initialDate: string;
}

const DatePicker = ({
  handleIncorrectDateChange,
  placeholder = "",
  onChange = () => {},
  toYear = new Date().getFullYear(),
  fromYear = new Date().getFullYear() - 80,
  displayMonth = new Date(new Date().getFullYear() - 30, 0),
  serverValidation = [],
  disabledDays,
  onEditDate = () => {},
  labelTop,
  className = "",
  initialDate,
}: DatePickerProps) => {
  const [invalidDateError, setInvalidDateError] = useState("");
  const [shouldShowDayPicker, setShouldShowDayPicker] = useState(false);
  const [selectedDate, setSelectedDate] = React.useState<Date>();
  const [inputDateValue, setInputDateValue] = useState(initialDate || "");
  const inputRef = useRef<HTMLInputElement>(null);
  const dayPickerWrapperRef = useRef<HTMLDivElement>(null);
  const datePickerHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { disabledBeforeDate, disabledAfterDate } = disabledDays || {};

  /**
   *This checks if the date typed is within the allowed period.
   * */
  const handleDisabledDays = (formattedDate: string, FORMAT: string): boolean => {
    if (!disabledDays) {
      return true;
    }
    try {
      const chosenDateParsed = parseDate(formattedDate, FORMAT);
      const beforeFormatted = formatDate(disabledBeforeDate || "", FORMAT);
      const afterFormatted = formatDate(disabledAfterDate || "", FORMAT);
      const beforeParsed = parseDate(beforeFormatted, FORMAT);
      const afterParsed = parseDate(afterFormatted, FORMAT);
      const beforeDiff = dateDiff(chosenDateParsed, beforeParsed, "d");
      const afterDiff = dateDiff(chosenDateParsed, afterParsed, "d");
      const isValid = afterDiff <= 0 && beforeDiff >= 0;
      return isValid;
    } catch (error) {
      // @ts-ignore
      logError(error);
      return false;
    }
  };

  const handleDatePicked = (date: Date | undefined) => {
    setShouldShowDayPicker(false);
    if (!date) return;
    setSelectedDate(date);
    const formattedDate = formatDate(date, "MM/DD/YYYY");
    handleDateTypedAndPicked(formattedDate);
  };

  /**
   * If either a valid date is typed, or selected,
   * ...then, onChange function is called,
   * ...and <DayPickerInput> is forced to hide.
   * */
  const handleDateTypedAndPicked = (date: string | undefined) => {
    if (date) {
      // `date` is always MM/DD/YYYY here (built by handleDatePickerEdit /
      // handleDatePicked). Parse with the matching format via dayjs
      // (customParseFormat) instead of the native `Date.parse`, whose result is
      // browser-dependent (Safari strictest) and can produce an Invalid Date —
      // which, fed to <DayPicker> below, was the crash vector noted above.
      // (dayjs directly, not the JS-typed parseDate, so .toDate()/.format() are typed.)
      const parsed = dayjs(date, "MM/DD/YYYY");
      // Validate strictly against the actual MM/DD/YYYY format (checkValidDate
      // uses dayjs customParseFormat in strict mode), which rejects impossible
      // dates that the non-strict parseDate would silently roll over.
      const isDateValid = checkValidDate(date, "MM/DD/YYYY");
      const formattedDate = isDateValid ? parsed.format(DISPLAY_DATE_FORMAT) : "";
      const isWithinDisabledDaysPeriod =
        disabledBeforeDate || disabledAfterDate
          ? handleDisabledDays(formattedDate, DISPLAY_DATE_FORMAT)
          : true;

      if (isWithinDisabledDaysPeriod && isDateValid) {
        setSelectedDate(parsed.toDate());
        onChange(date);
        setInputDateValue(formattedDate);
        setInvalidDateError("");
        setShouldShowDayPicker(false);
      } else {
        setInvalidDateError("Invalid Date");
        handleIncorrectDateChange();
      }
    }
  };

  /**
   * This function handles the each edit a customer types into the (alias) date input field.
   */
  const handleDatePickerEdit = () => {
    const newValue = maskDate(inputRef);
    // This check fixes an issue where a user cannot remove / from the date input field.
    setInputDateValue(newValue);
    onEditDate();

    const dateData = newValue.split("/");
    const [day, month, year] = dateData;
    const isDateComplete = newValue.length === 10;

    if (isDateComplete) {
      const dateUSFormat = `${month}/${day}/${year}`; // For 'dayjs' to validate the date.
      handleDateTypedAndPicked(dateUSFormat);
    }
  };

  const handleOnBlur = (e: React.FocusEvent) => {
    const relatedTarget = e.relatedTarget as Node | null;
    if (relatedTarget && dayPickerWrapperRef.current?.contains(relatedTarget)) {
      return;
    }
    datePickerHideTimeoutRef.current = setTimeout(() => {
      setShouldShowDayPicker(false);
    }, 200);
  };

  const handleDatePickerTimeoutClearIfVisible = () => {
    if (datePickerHideTimeoutRef.current) {
      clearTimeout(datePickerHideTimeoutRef.current);
      datePickerHideTimeoutRef.current = null;
    }
  };

  // Prevent mousedown from stealing focus away from the input field, which
  // would trigger the blur handler and close the picker. We allow default
  // behavior for <select> and <option> elements so the month/year dropdowns
  // in the date picker caption still work normally.
  const handleDayPickerMouseDown = (e: React.MouseEvent) => {
    const { target } = e;
    if (!(target instanceof HTMLSelectElement || target instanceof HTMLOptionElement)) {
      e.preventDefault();
    }
  };

  // Belt-and-braces: even though selectedDate is now only set from a validated
  // parse, never hand react-day-picker a NaN-time date (also guards a bad
  // `displayMonth` passed by a caller, e.g. toDate() of an invalid value).
  const safeSelectedDate = isValidDate(selectedDate) ? selectedDate : undefined;
  const safeDisplayMonth = isValidDate(displayMonth)
    ? displayMonth
    : new Date(new Date().getFullYear() - 30, 0);

  return (
    <>
      {shouldShowDayPicker && (
        <DayPickerWrapper
          ref={dayPickerWrapperRef}
          onMouseDown={handleDayPickerMouseDown}
          onClick={() => handleDatePickerTimeoutClearIfVisible()}
          className={className}
        >
          <DayPickerBackground>
            <DayPicker
              mode="single"
              captionLayout="dropdown-buttons"
              selected={safeSelectedDate}
              onSelect={(date) => handleDatePicked(date)}
              defaultMonth={safeSelectedDate || safeDisplayMonth}
              fromDate={disabledBeforeDate}
              toDate={disabledAfterDate}
              fromYear={disabledBeforeDate ? undefined : fromYear}
              toYear={disabledAfterDate ? undefined : toYear}
            />
          </DayPickerBackground>
        </DayPickerWrapper>
      )}
      <AliasDatePickerHolder>
        <label htmlFor="datePicker" onClick={() => setShouldShowDayPicker(true)}>
          <DateInput
            id="datePicker"
            ref={inputRef}
            placeholder={placeholder || DISPLAY_DATE_FORMAT.toUpperCase()}
            onChange={() => handleDatePickerEdit()}
            onFocus={() => setShouldShowDayPicker(true)}
            value={inputDateValue}
            maxLength={10}
            onBlur={(e) => handleOnBlur(e)}
            autoComplete="off"
            hasLabelTop={!!labelTop}
          />
          {labelTop && <DateInputFieldLabel>{labelTop}</DateInputFieldLabel>}
          <DatePickerIconHolder onClick={() => handleDatePickerTimeoutClearIfVisible()}>
            <DatePickerIcon src={calendarIcon} alt="calendarIcon" />
          </DatePickerIconHolder>
        </label>
      </AliasDatePickerHolder>
      {invalidDateError && <InputErrorBox>{invalidDateError}</InputErrorBox>}
      {serverValidation?.[0] && <div className="error">{serverValidation[0]}</div>}
    </>
  );
};

export default DatePicker;
